import { create } from 'zustand';
import { threeCollectionWorkflowService } from '@/domains/workflow/services/threeCollectionWorkflowService';
import type { ProgramInfo, TeamInfo } from '@/domains/workflow/types/threeCollectionWorkflow';

interface ProgramsStore {
  programs: ProgramInfo[];
  allTeamsGlobal: TeamInfo[];

  isLoadingPrograms: boolean;
  isLoadingGlobalTeams: boolean;

  programsError: string | null;
  globalTeamsError: string | null;

  loadPrograms: () => Promise<void>;
  loadAllTeamsGlobal: () => Promise<void>;
}

// In-flight promise tracking for deduplication
let programsPromise: Promise<void> | null = null;
let globalTeamsPromise: Promise<void> | null = null;

export const useProgramsStore = create<ProgramsStore>()((set, get) => ({
  programs: [],
  allTeamsGlobal: [],

  isLoadingPrograms: false,
  isLoadingGlobalTeams: false,

  programsError: null,
  globalTeamsError: null,

  loadPrograms: async () => {
    // Deduplication: return existing in-flight promise
    if (programsPromise) return programsPromise;

    set({ isLoadingPrograms: true, programsError: null });

    programsPromise = (async () => {
      try {
        const programs = await threeCollectionWorkflowService.getProgramsInfo();
        set({
          programs: programs.sort((a, b) => a.program_name.localeCompare(b.program_name)),
          isLoadingPrograms: false,
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load programs';
        set({ programsError: errorMessage, isLoadingPrograms: false });
        throw error;
      } finally {
        programsPromise = null;
      }
    })();

    return programsPromise;
  },

  loadAllTeamsGlobal: async () => {
    // Deduplication: return existing in-flight promise
    if (globalTeamsPromise) return globalTeamsPromise;

    // Ensure programs are loaded first
    const { programs, isLoadingPrograms } = get();
    if (programs.length === 0 && !isLoadingPrograms) {
      await get().loadPrograms();
    } else if (isLoadingPrograms && programsPromise) {
      await programsPromise;
    }

    set({ isLoadingGlobalTeams: true, globalTeamsError: null });

    globalTeamsPromise = (async () => {
      try {
        const currentPrograms = get().programs;

        const allTeamsArrays = await Promise.all(
          currentPrograms.map(program =>
            threeCollectionWorkflowService.getTeamsInfo(program.program_id).catch(() => [])
          )
        );

        const allTeams = allTeamsArrays
          .flat()
          .sort((a, b) => a.team_name.localeCompare(b.team_name));

        set({ allTeamsGlobal: allTeams, isLoadingGlobalTeams: false });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load global teams';
        set({ globalTeamsError: errorMessage, isLoadingGlobalTeams: false });
        throw error;
      } finally {
        globalTeamsPromise = null;
      }
    })();

    return globalTeamsPromise;
  },
}));
